import { DonwloadIcon, TrashCan } from '../../../icons/SvgIcons';
import { TableHeader } from '../../tables/AISystemsTable/components/TableHeader/TableHeader';
import {
  setPreviewDocument,
  setDocuments,
  setCreateAnnexIVDraftResponse,
  setAnnexIVFieldsState,
  setCreateEUDeclarationResponse,
  setEUDeclarationFieldsState,
  setCreateFRIAResponse,
  setFRIAFieldsState,
  setEUDeclarationStatus,
  setDocumentationReqBody,
  setCreateDPIAResponse,
  setDPIAFieldsState
} from '../../../store/slices/documentationSlice';
import {
  handleOpenSidePanel,
  setSidePanelContent,
  setSidePanelTitle
} from '../../../store/slices/sidePanelSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { useAuthorization } from '../../../hooks/useAuthorization';
import { format } from 'date-fns';
import {
  downloadDocument,
  deleteDocument
} from '../../../services/DocumentationService';
import { useCallback, useEffect, useRef, useState } from 'react';
import ModalDeleteConfirmation from '../../ui/Modal/ModalDeleteConfirmation';
import {
  DocumentationParams,
  DocumentResponse
} from '../../../types/Documentation.types';
import { getAnnexIVByAiSystem } from '../../../services/AnnexIVService';
import { getEUDeclarationByAiSystem } from '../../../services/EUDeclarationService';
import {
  mapAnnexContentToFields,
  mapDPIAContentToFields,
  mapEUDeclarationContentToFields,
  mapFRIAContentToFields
} from '../../../utils/documentationHelpers';
import {
  getFRIAByAiSystem,
  getFRIAByDocumentId
} from '../../../services/FRIAService';
import { showToast } from '../../../store/slices/toastSlice';

import Skeleton from 'react-loading-skeleton';
import { EmptyState } from '../../ui/EmptyState/EmptyState';
import SearchIcon from '../../../assets/search_icon.svg';
import {
  getDPIAByAiSystem,
  getDPIAByDocumentId
} from '../../../services/DPIAService';
import { fetchRequirementStepsByAISystemId } from '../../../store/slices/requirementStepsSlice';

type Category =
  | 'RISK_ASSESSMENT'
  | 'ANNEX_IV'
  | 'CONFORMITY_ASSESSMENT'
  | 'EU_DECLARATION_OF_CONFORMITY'
  | 'FRIA'
  | 'DPIA'
  | 'OTHER';

const categoryDisplayNames: Record<Category, string> = {
  RISK_ASSESSMENT: 'Risk Assessment',
  ANNEX_IV: 'Annex IV',
  CONFORMITY_ASSESSMENT: 'Conformity Assessment',
  EU_DECLARATION_OF_CONFORMITY: 'EU Declaration of Conformity',
  FRIA: 'Fria',
  DPIA: 'Dpia',
  OTHER: 'Other'
};

const SIGNABLE_DRAFT_CATEGORIES: Category[] = [
  'ANNEX_IV',
  'EU_DECLARATION_OF_CONFORMITY'
];

export const DocumentationTable = () => {
  const dispatch = useAppDispatch();
  const { can, roleResolved } = useAuthorization();

  const {
    documents,
    documentsLoading,
    documentsTotal,
    documentationReqBody,
    order
  } = useAppSelector((state) => state.documentation);
  const {
    steps,
    loading: requirementStepsLoading,
    aiSystemId: requirementStepsAiSystemId
  } = useAppSelector((state) => state.requirementSteps);
  const canManageDocumentation = roleResolved && can('aiSystems.documentation.delete');

  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<{
    id: string;
    fileName: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [sortOption, setSortOption] = useState<{
    order: DocumentationParams['order'];
    sortBy: DocumentationParams['sortBy'];
  }>({ order: 'desc', sortBy: 'createdAt' });

  const isAnnexIVGenerated = documents.some(
    (doc) => doc.category === 'ANNEX_IV'
  );

  const isDeclarationGenerated = documents.some(
    (doc) => doc.category === 'EU_DECLARATION_OF_CONFORMITY'
  );

  const isFriaGenerated = documents.some((doc) => doc.category === 'FRIA');

  const isDpiaGenerated = documents.some((doc) => doc.category === 'DPIA');

  const isSupportingDocsCompleted = steps.some(
    (step) =>
      step.key === 'SUPPORTING_DOCUMENTATION' && step.status === 'COMPLETED'
  );

  const observer = useRef<IntersectionObserver | null>(null);
  const lastElement = useCallback(
    (node: HTMLDivElement | null) => {
      if (documentsLoading) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        const isVisible = entries.some((e) => e.isIntersecting);
        const hasMore = documents.length < documentsTotal;

        if (isVisible && hasMore) {
          dispatch(
            setDocumentationReqBody({
              ...documentationReqBody,
              offset: documents.length
            })
          );
        }
      });
      if (node) observer.current.observe(node);
    },
    [
      documentsLoading,
      documents.length,
      documentsTotal,
      dispatch,
      documentationReqBody
    ]
  );

  const isSigningLocked = (document: DocumentResponse) => {
    if (!document.isDraft) {
      return false;
    }

    const category = document.category as Category;
    return (
      SIGNABLE_DRAFT_CATEGORIES.includes(category) &&
      document.signingStatus === 'pending'
    );
  };

  const renderSigningBadge = (document: DocumentResponse) => {
    if (!isSigningLocked(document)) {
      return null;
    }

    return (
      <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-800">
        Signing in progress
      </span>
    );
  };

  const tdClasses = 'px-6 py-3 text-sm text-gray-550 text-left';

  const handleDownload = async (e: React.MouseEvent, documentId: string) => {
    e.stopPropagation();

    try {
      setDownloadingId(documentId);
      const docData = await downloadDocument(documentId);

      if (docData.downloadUrl) {
        // Open the presigned URL in a new tab to trigger download
        const link = document.createElement('a');
        link.href = docData.downloadUrl;
        link.download = docData.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.warn('No download URL available for document:', documentId);
      }
    } catch (error) {
      console.error('Error downloading document:', error);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (
    e: React.MouseEvent,
    documentId: string,
    fileName: string
  ) => {
    if (!canManageDocumentation) {
      return;
    }

    e.stopPropagation();
    setDocumentToDelete({ id: documentId, fileName });
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!documentToDelete) return;

    try {
      setIsDeleting(true);
      await deleteDocument(documentToDelete.id);

      // Remove document from local state
      const updatedDocuments = documents.filter(
        (doc) => doc.id !== documentToDelete.id
      );
      dispatch(
        setDocuments({
          items: updatedDocuments,
          total: documentsTotal - 1
        })
      );

      setDeleteModalOpen(false);
      setDocumentToDelete(null);
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const closeDeleteModal = () => {
    if (!isDeleting) {
      setDeleteModalOpen(false);
      setDocumentToDelete(null);
    }
  };

  const openAnnexDraft = async (document: DocumentResponse) => {
    try {
      setDownloadingId(document.id);
      const annex = await getAnnexIVByAiSystem(document.aiSystemId);
      dispatch(setCreateAnnexIVDraftResponse(annex));
      dispatch(setAnnexIVFieldsState(mapAnnexContentToFields(annex)));
      dispatch(setSidePanelTitle('Annex IV – Technical Documentation (Draft)'));
      dispatch(setSidePanelContent('ANNEX_IV'));
      dispatch(handleOpenSidePanel(true));
    } catch (error) {
      console.error('Error opening Annex IV draft:', error);
    } finally {
      setDownloadingId(null);
    }
  };

  const openEUDeclarationDraft = async (document: DocumentResponse) => {
    try {
      setDownloadingId(document.id);
      const declaration = await getEUDeclarationByAiSystem(document.aiSystemId);
      dispatch(setCreateEUDeclarationResponse(declaration));
      dispatch(
        setEUDeclarationFieldsState(
          mapEUDeclarationContentToFields(declaration)
        )
      );
      dispatch(setSidePanelTitle('EU Declaration of Conformity (Draft)'));
      dispatch(setSidePanelContent('EU_DECLARATION'));
      dispatch(handleOpenSidePanel(true));
    } catch (error) {
      console.error('Error opening EU Declaration draft:', error);
    } finally {
      setDownloadingId(null);
    }
  };

  const openFRIADraft = async (
    document: DocumentResponse
  ): Promise<boolean> => {
    try {
      setDownloadingId(document.id);
      let fria;
      try {
        // This endpoint returns the canonical FRIA draft payload with FRIA id.
        fria = await getFRIAByAiSystem(document.aiSystemId, {
          suppressErrorToast: true
        });
      } catch {
        // Fallback for document-driven opening if AI-system lookup fails.
        fria = await getFRIAByDocumentId(document.id);
      }

      if (!fria?.id) {
        console.error('FRIA draft response missing id:', fria);
        return false;
      }

      dispatch(setCreateFRIAResponse(fria));
      dispatch(setFRIAFieldsState(mapFRIAContentToFields(fria)));
      dispatch(setSidePanelTitle('FRIA Document (Draft)'));
      dispatch(setSidePanelContent('FRIA'));
      dispatch(handleOpenSidePanel(true));
      return true;
    } catch (error) {
      console.error('Error opening FRIA draft:', error);
      return false;
    } finally {
      setDownloadingId(null);
    }
  };

  const openDPIADraft = async (
    document: DocumentResponse
  ): Promise<boolean> => {
    try {
      setDownloadingId(document.id);
      let dpia;
      try {
        // This endpoint returns the canonical DPIA draft payload with DPIA id.
        dpia = await getDPIAByAiSystem(document.aiSystemId, {
          suppressErrorToast: true
        });
      } catch {
        // Fallback for document-driven opening if AI-system lookup fails.
        dpia = await getDPIAByDocumentId(document.id);
      }

      if (!dpia?.id) {
        console.error('DPIA draft response missing id:', dpia);
        return false;
      }

      dispatch(setCreateDPIAResponse(dpia));
      dispatch(setDPIAFieldsState(mapDPIAContentToFields(dpia)));
      dispatch(setSidePanelTitle('DPIA Document (Draft)'));
      dispatch(setSidePanelContent('DPIA'));
      dispatch(handleOpenSidePanel(true));
      return true;
    } catch (error) {
      console.error('Error opening DPIA draft:', error);
      return false;
    } finally {
      setDownloadingId(null);
    }
  };

  const handleRowClick = async (document: DocumentResponse) => {
    if (isSigningLocked(document)) {
      dispatch(
        showToast({
          message:
            'Signing is still in progress. Please wait until it completes before editing this draft.',
          type: 'info'
        })
      );
      return;
    }

    if (document.isDraft) {
      if (!canManageDocumentation) {
        dispatch(
          showToast({
            message:
              'You do not have permission to edit draft documents for this AI system.',
            type: 'error'
          })
        );
        return;
      }

      if (document.category === 'ANNEX_IV') {
        await openAnnexDraft(document);
        return;
      }

      if (document.category === 'EU_DECLARATION_OF_CONFORMITY') {
        await openEUDeclarationDraft(document);
        return;
      }

      const isPotentialFRIADraft =
        document.category === 'RISK_ASSESSMENT' ||
        document.fileName.toUpperCase().includes('FRIA');

      if (isPotentialFRIADraft) {
        const opened = await openFRIADraft(document);
        if (opened) {
          return;
        }
      }

      const isPotentialDPIADraft =
        document.category === 'RISK_ASSESSMENT' ||
        document.fileName.toUpperCase().includes('DPIA');

      if (isPotentialDPIADraft) {
        const opened = await openDPIADraft(document);
        if (opened) {
          return;
        }
      }
    }

    try {
      setDownloadingId(document.id);
      const docData = await downloadDocument(document.id);
      dispatch(setPreviewDocument(docData));
      dispatch(setSidePanelTitle(document.fileName));
      dispatch(setSidePanelContent('DOCUMENT_PREVIEW'));
      dispatch(handleOpenSidePanel(true));
    } catch (error) {
      console.error('Error loading document preview:', error);
    } finally {
      setDownloadingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return format(date, 'dd MMM yyyy, HH:mm');
  };

  const getCategory = (category: Category) => {
    const categoryClasses: Record<Category, string> = {
      RISK_ASSESSMENT: 'bg-purple-50 text-purple-950',
      ANNEX_IV: 'bg-blue-100 text-blue-700',
      CONFORMITY_ASSESSMENT: 'bg-purple-100 text-purple-700',
      EU_DECLARATION_OF_CONFORMITY: 'bg-purple-60 text-purple-960',
      FRIA: 'bg-blue-100 text-blue-700',
      DPIA: 'bg-purple-100 text-purple-700',
      OTHER: 'bg-gray-100 text-gray-800'
    };
    return (
      <div
        className={`w-fit px-2.5 py-0.5 rounded-[10px] text-xs font-medium ${categoryClasses[category]}`}
      >
        {categoryDisplayNames[category]}
      </div>
    );
  };

  const handleSort = (sortBy: string) => {
    setSortOption((prev) => {
      const newOrder =
        prev.sortBy === sortBy && prev.order === 'asc' ? 'desc' : 'asc';
      return { sortBy, order: newOrder };
    });

    dispatch(
      setDocumentationReqBody({
        ...documentationReqBody,
        order: sortOption.order,
        sortBy: sortOption.sortBy
      })
    );
    return order;
  };

  const disableDeleteDocument = (document: DocumentResponse) => {
    if (
      document.source === 'UPLOADED' &&
      steps.length === 2 &&
      isSupportingDocsCompleted
    )
      return true;

    if (
      document.source === 'GENERATED' ||
      document.category === 'FRIA' ||
      document.category === 'DPIA'
    )
      return true;

    if (document.category === 'CONFORMITY_ASSESSMENT')
      return isDeclarationGenerated;

    if (document.category === 'OTHER') {
      return isAnnexIVGenerated || isFriaGenerated || isDpiaGenerated;
    }

    return false;
  };

  useEffect(() => {
    if (
      !documentationReqBody.aiSystemId ||
      requirementStepsAiSystemId === documentationReqBody.aiSystemId ||
      requirementStepsLoading
    )
      return;

    dispatch(
      fetchRequirementStepsByAISystemId(documentationReqBody.aiSystemId)
    );
  }, [
    documentationReqBody.aiSystemId,
    requirementStepsAiSystemId,
    requirementStepsLoading,
    dispatch
  ]);

  useEffect(() => {
    const euDeclarationExist =
      documents.filter((doc) => doc.category === 'EU_DECLARATION_OF_CONFORMITY')
        .length !== 0;

    const isEUDeclarationDraft = documents.some(
      (doc) =>
        doc.category === 'EU_DECLARATION_OF_CONFORMITY' && doc.isDraft === true
    );

    if (!euDeclarationExist) {
      dispatch(setEUDeclarationStatus('NOT_STARTED'));
    } else if (isEUDeclarationDraft) {
      dispatch(setEUDeclarationStatus('IN_PROGRESS'));
    } else {
      dispatch(setEUDeclarationStatus('COMPLETED'));
    }
  }, [documents]);

  if (documentsLoading && documents.length === 0) {
    return (
      <div className="w-full p-8 text-center text-gray-500">
        Loading documents...
      </div>
    );
  }

  if (!documents || documents.length === 0) {
    return (
      <EmptyState
        icon={<img src={SearchIcon} alt="No data" className="w-12 h-12" />}
        title="No AI Systems matched your criteria."
        description="Try adjusting your search or filters."
        classes="h-[calc(48vh)]"
      />
    );
  }

  return (
    <div className="w-full space-y-4">
      <div className="w-full max-h-[calc(48vh)] overflow-y-auto rounded-lg bg-white shadow-sm border border-solid border-gray-250v2">
        <table className="w-full">
          <div className="flex sticky top-0 border-b border-gray-100">
            <TableHeader
              type="Documentation"
              onSort={handleSort}
              sortOrder={sortOption.order}
              sortBy={sortOption.sortBy}
            />
          </div>
          <tbody>
            {documents.map((doc, index) => {
              const signingLocked = isSigningLocked(doc);
              return (
                <tr
                  key={doc.id}
                  className={`w-full h-14 flex items-center ${
                    signingLocked
                      ? 'cursor-not-allowed bg-gray-50 opacity-70'
                      : 'cursor-pointer bg-white hover:bg-gray-50'
                  }
                    ${index === 0 ? '' : 'border-t border-gray-100'}
                      `}
                  onClick={() => handleRowClick(doc)}
                >
                  <td
                    className={`${tdClasses} w-1/3 truncate text-zinc-900 font-medium`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="truncate">{doc.fileName}</span>
                      {renderSigningBadge(doc)}
                    </span>
                  </td>
                  <td className={`${tdClasses} w-1/6`}>
                    {doc.source === 'UPLOADED' ? 'Uploaded' : 'Generated'}
                  </td>
                  <td className={`${tdClasses} w-1/4`}>
                    {getCategory(doc.category as Category)}
                  </td>
                  <td className={`${tdClasses} w-1/5`}>
                    {formatDate(doc.createdAt)}
                  </td>
                  <td className={`${tdClasses} w-32 flex items-center gap-6`}>
                    <button
                      id={`documentation-download-button-${doc.id}`}
                      data-testid={`documentation-download-button-${doc.id}`}
                      onClick={(e) => handleDownload(e, doc.id)}
                      disabled={downloadingId === doc.id || doc.isDraft}
                      className="disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {DonwloadIcon()}
                    </button>
                    {doc.category !== 'RISK_ASSESSMENT' && (
                      <button
                        id={`documentation-delete-button-${doc.id}`}
                        data-testid={`documentation-delete-button-${doc.id}`}
                        onClick={(e) => handleDelete(e, doc.id, doc.fileName)}
                        disabled={
                          downloadingId === doc.id ||
                          disableDeleteDocument(doc) ||
                          !canManageDocumentation
                        }
                        className="disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {TrashCan(
                          'medium',
                          disableDeleteDocument(doc) || !canManageDocumentation
                        )}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {documentsTotal > documents.length && (
              <tr ref={lastElement}>
                <td>
                  <Skeleton
                    height={44}
                    className="rounded-none border-t border-gray-100"
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ModalDeleteConfirmation
        open={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title="Delete Document?"
        description="Are you sure you want to delete"
        itemName={documentToDelete?.fileName}
        isLoading={isDeleting}
      />
    </div>
  );
};
